import type { TimeFrequency } from '@epilot/pricing'
import {
  formatFeeAmountFromString,
  formatPriceUnit,
  getAmountWithTax
} from '@epilot/pricing'
import type { TFunction } from 'i18next'

import classes from './BaseTable.module.scss'
import { usePriceGetAgDetailsContext } from './PriceGetAgDetailsContext'
import { getNormalizedAmountDecimal, getYearlyDecimalAmount } from './utils'

export type PriceTableRow = {
  label: string
  isEmpty?: boolean
  tax?: number
  yearlyAmountDecimal?: string
  // amounts based on the billing period
  amountDecimal?: string
  unitAmountDecimal?: string
}

type BaseTableProps = {
  title: string
  rows: PriceTableRow[]
}

export const BaseTable = ({ title, rows }: BaseTableProps) => {
  const { t, userLocale, currency, billingPeriod, unit, unitPricePeriod } =
    usePriceGetAgDetailsContext()

  return (
    <div className={classes.tableContainer}>
      <table className={classes.priceTable}>
        <thead>
          <tr>
            <th title={t(`price_get_ag_details.table.groups.${title}`, title)}>
              {t(`price_get_ag_details.table.groups.${title}`, title)}
            </th>
            <th title={t('getag_details_table.price_unit', 'Unit Price')}>
              {t('getag_details_table.price_unit', 'Unit Price')}
            </th>
            <th
              title={t(
                'table_order.recurrences.billing_period.yearly',
                'yearly'
              )}
            >
              {t('table_order.recurrences.billing_period.yearly', 'yearly')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <TableRow
              billingPeriod={billingPeriod}
              currency={currency}
              index={index}
              key={row.label || index}
              row={row}
              t={t}
              unit={unit}
              unitPricePeriod={unitPricePeriod}
              userLocale={userLocale}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TableRow = ({
  row,
  index,
  billingPeriod,
  t,
  userLocale,
  currency,
  unit,
  unitPricePeriod
}: {
  row: PriceTableRow
  billingPeriod: TimeFrequency
  t: TFunction
  index: number
  userLocale: string
  currency: string
  unit?: string
  unitPricePeriod: TimeFrequency
}) => {
  const amountDecimal = row.tax
    ? getAmountWithTax(row.amountDecimal, row.tax)
    : row.amountDecimal

  const unitAmountDecimal = row.tax
    ? getAmountWithTax(row.unitAmountDecimal, row.tax)
    : row.unitAmountDecimal

  const yearlyAmountDecimal = row.yearlyAmountDecimal
    ? row.tax
      ? getAmountWithTax(row.yearlyAmountDecimal, row.tax)
      : row.yearlyAmountDecimal
    : undefined

  const formattedUnit = formatPriceUnit(unit)

  return (
    <tr key={row.label || index}>
      <td>{t(row.label, row.label)}</td>
      <td>
        {!row.isEmpty
          ? unitAmountDecimal
            ? formatFeeAmountFromString({
                decimalAmount: unitAmountDecimal,
                currency,
                locale: userLocale,
                enableSubunitDisplay: true
              }) + `/${formattedUnit}`
            : amountDecimal
              ? formatFeeAmountFromString({
                  decimalAmount: getNormalizedAmountDecimal(
                    amountDecimal,
                    billingPeriod,
                    unitPricePeriod
                  ),
                  currency,
                  locale: userLocale,
                  enableSubunitDisplay: true
                }) +
                ` ${t(
                  `product.selection.recurring.${unitPricePeriod}`,
                  unitPricePeriod
                )}`
              : '-'
          : '-'}
      </td>
      <td>
        {!row.isEmpty && amountDecimal
          ? formatFeeAmountFromString({
              decimalAmount: yearlyAmountDecimal
                ? yearlyAmountDecimal
                : getYearlyDecimalAmount(amountDecimal, billingPeriod),
              currency,
              locale: userLocale
            })
          : '-'}
      </td>
    </tr>
  )
}
